import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CompanyResponse, CompanyDocumentResponse } from '../../../../@shared/models/response.module';
import { EndpointService } from '../../../../@shared/endpoints.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AddDocument } from './components/add-document/add-document';
import { DeleteModal } from '../../../../@shared/components/delete-modal/delete-modal';
import { ToastService } from '../../../../@core/services/toast.service';

@Component({
  selector: 'app-company-profile-documents',
  standalone: false,
  templateUrl: './company-profile-documents.html',
  styleUrl: './company-profile-documents.css'
})
export class CompanyProfileDocuments implements OnChanges {
  @Input() company: CompanyResponse;
  @Output() dataChanged = new EventEmitter<void>();

  documents: CompanyDocumentResponse[] = [];
  loading: boolean = false;

  constructor(
    private endpointService: EndpointService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['company'] && this.company?.company_id) {
      this.getDocuments();
    }
  }

  getDocuments() {
    if (!this.company?.company_id) {
      return;
    }
    this.loading = true;
    this.endpointService.cor_coy_27({ company_id: this.company.company_id }).subscribe({
      next: (res) => {
        this.documents = res.data || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.documents = [];
      }
    });
  }

  openAddModal() {
    const modalRef = this.modalService.open(AddDocument, { size: 'lg', centered: true });
    modalRef.componentInstance.company = this.company;
    modalRef.result.then((result) => {
      if (result) {
        this.getDocuments();
        this.dataChanged.emit();
      }
    }).catch(() => {});
  }

  openEditModal(document: CompanyDocumentResponse) {
    const modalRef = this.modalService.open(AddDocument, { size: 'lg', centered: true });
    modalRef.componentInstance.company = this.company;
    modalRef.componentInstance.editMode = true;
    modalRef.componentInstance.document = document;
    modalRef.result.then((result) => {
      if (result) {
        this.getDocuments();
        this.dataChanged.emit();
      }
    }).catch(() => {});
  }

  deleteDocument(document: CompanyDocumentResponse) {
    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.description = this.translateService.instant('companyProfile.documents.deleteConfirm');
    modalRef.result.then((result) => {
      if (result) {
        this.endpointService.cor_coy_30(document.document_id, { company_id: this.company.company_id, document_id: document.document_id }).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('companyProfile.documents.deleteSuccess'));
            this.getDocuments();
            this.dataChanged.emit();
          },
          error: (err) => {
            console.error('Error deleting document:', err);
            this.toastService.error(this.translateService.instant('companyProfile.documents.deleteError'));
          }
        });
      }
    }).catch(() => {});
  }

  downloadDocument(document: CompanyDocumentResponse) {
    if (document.document_url) {
      window.open(document.document_url, '_blank');
    }
  }

  formatFileSize(bytes: number | null | undefined): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getFileIcon(extension: string | null | undefined): string {
    switch (extension?.toLowerCase()) {
      case 'pdf': return 'fa-file-pdf text-danger';
      case 'doc':
      case 'docx': return 'fa-file-word text-primary';
      case 'xls':
      case 'xlsx': return 'fa-file-excel text-success';
      case 'ppt':
      case 'pptx': return 'fa-file-powerpoint text-warning';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fa-file-image text-info';
      case 'zip':
      case 'rar': return 'fa-file-archive text-secondary';
      default: return 'fa-file text-muted';
    }
  }
}
